import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import parse from 'html-react-parser';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import yn from 'yn';
import { normaliseSchemaGraphUrls } from './canonical-url';
import {
  APP_PAYMENT_GATEWAYS,
  FFH_EXTERNAL_ID,
  FFH_PROFILE_ID,
  FFH_HOSTNAME,
  FFH_ROLES,
  MEMBERSHIPS,
  NAV_DATA,
  NEW_USER_THRESHOLD_2026,
  PAID_MEMBERSHIP_LEVELS,
  TIER_TO_MEMBERSHIP
} from './constants';
import { PlayerType } from './player-functions';
import { Auth0UserInfo } from './auth0/utils';
import type { CheckoutReturnPath } from './checkout/return-paths';
import { ARM_COUPON_QUERY_PARAM } from './checkout/arm-coupon';
import { Transaction } from '@components/Account/types';

dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);

const isTagByName = (domNode, name) =>
  domNode.type === 'tag' && domNode.name === name;

const isEmbeddedVideo = (domNode) => {
  if (domNode && domNode.attribs && domNode.attribs.src) {
    return (
      domNode.attribs.src.includes('player.vimeo.com') ||
      domNode.attribs.src.includes('www.youtube.com') ||
      domNode.attribs.src.includes('soundcloud')
    );
  }
};

const getTextFromDomNode = (domNode) => {
  if (
    domNode.children[0] &&
    domNode.children[0].type === 'tag' &&
    (domNode.children[0].name === 'strong' || domNode.children[0].name === 'b')
  ) {
    return <b>{domNode.children[0].children[0].data}</b>;
  }
  return domNode.children[0].data;
};

const isLinkToArticle = (domNode) =>
  domNode.attribs &&
  domNode.attribs.href &&
  domNode.attribs.href.includes(process.env.NEXT_PUBLIC_MEMBERPRESS_BASE_URL);

const isMatchingLink = (domNode, str) => {
  if (
    domNode.attribs &&
    domNode.attribs.href &&
    domNode.attribs.href.includes(str)
  ) {
    return true;
  }

  return false;
};

const isCurrentPath = (path, navItem) => {
  if (path.startsWith('/my-team') && navItem.url === '/my-team') {
    return true;
  }

  if (path.includes(navItem.url) && !navItem.root) {
    return true;
  }

  const matchingNav = NAV_DATA.find((a) => a.url == path);

  if (!matchingNav) return false;
  const topLevel = navItem.level.split('.')[0];
  const matchingNavLevel = matchingNav.level.split('.')[0];

  if (topLevel === matchingNavLevel) {
    return true;
  }

  return false;
};

const isCurrentlyActive = (path, navItem) => {
  if (path === navItem.url) {
    return true;
  }

  return false;
};

const isInActiveHierachy = (path, navItem) => {
  const matchingNav = NAV_DATA.find((a) => a.url === path);

  if (!matchingNav) return false;
  const topLevel = navItem.level.split('.')[0];
  const matchingNavLevel = matchingNav.level.split('.')[0];

  if (topLevel === matchingNavLevel) {
    return true;
  }

  return false;
};

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const isParagraphWrappingIframe = (domNode) =>
  domNode.name === 'p' &&
  domNode.children &&
  isEmbeddedVideo(domNode.children[0]);

export const ROLE_LEVELS = {
  FREE: 0,
  STARTER: 1,
  PRO: 3,
  ULTRA: 5
} as const;

const isPaidMember = (user) => {
  if (!user) return false;

  const roles = user[FFH_ROLES] || [];
  const userHighestLevel = Math.max(
    ...roles.map((role) => ROLE_LEVELS[role] || 0),
    0
  );

  return userHighestLevel > 0;
};

// Canonical, exactly-capitalised membership tier names. CleverTap segment
// filters are case-sensitive, so these values must never be lower-cased
// (see FFH-1049 / CleverTap Phase 1 reference §4.4).
const CLEVERTAP_TIER_NAMES = {
  FREE: 'Free',
  STARTER: 'Starter',
  PRO: 'Pro',
  ULTRA: 'Ultra'
} as const;

/**
 * Resolves the user's current canonical membership tier for CleverTap events.
 * Returns the exactly-capitalised tier of their highest role, defaulting to
 * 'Free' for logged-out, role-less or pre-auth users.
 */
const getCleverTapMembershipTier = (user): string => {
  const roles: string[] = user?.[FFH_ROLES] || [];
  const highestRole = roles.reduce(
    (highest, role) =>
      (ROLE_LEVELS[role] ?? -1) > (ROLE_LEVELS[highest] ?? -1) ? role : highest,
    'FREE'
  );
  return CLEVERTAP_TIER_NAMES[highestRole] ?? CLEVERTAP_TIER_NAMES.FREE;
};

/**
 * Normalises a plan/tier label (e.g. a join table header) to its canonical,
 * exactly-capitalised CleverTap tier name, or undefined if unrecognised.
 */
const getCleverTapTierName = (name?: string): string | undefined => {
  if (!name) return undefined;
  return CLEVERTAP_TIER_NAMES[name.trim().toUpperCase()];
};

const getIsFreeMember = (user) => {
  if (!user) return false;

  const roles = user[FFH_ROLES] || [];
  const userHighestLevel = Math.max(
    ...roles.map((role) => ROLE_LEVELS[role] || 0),
    0
  );

  return userHighestLevel === 0;
};

/**
 * The FFH staff role, as it appears in the Auth0 roles claim.
 *
 * Deliberately absent from ROLE_LEVELS: it is not a membership tier and
 * confers no paid entitlement, so it must never be folded into the level
 * comparison that gates paid content.
 */
const ADMIN_ROLE = 'admin';

/**
 * Whether this is an FFH staff account.
 *
 * Read from the Auth0 session, so it is available on the first render, needs
 * no analytics consent, no third-party SDK and no network call of its own, and
 * works identically on every deployment including per-commit previews. The
 * roles claim is issued server-side by Auth0, which makes this a real
 * authorisation signal rather than something the client asserts about itself.
 */
const hasAdminRole = (user): boolean => {
  if (!user) return false;
  const roles: unknown = user[FFH_ROLES];
  // Checked rather than assumed, because this one fails open if it is wrong.
  // `String.prototype.includes` exists too, so a claim that arrived as a bare
  // string would match on a substring and hand 'notadmin' the harness. The
  // neighbouring helpers call `.map` and would throw on the same input; this
  // one would quietly say yes.
  if (!Array.isArray(roles)) return false;
  return roles.includes(ADMIN_ROLE);
};

const hasSufficientPrivileges = (user, minimumMembershipLevel: number) => {
  if (!user) return false;

  const roles = user[FFH_ROLES] || [];
  const userHighestLevel = Math.max(
    ...roles.map((role) => ROLE_LEVELS[role] || 0),
    0
  );

  return userHighestLevel >= minimumMembershipLevel;
};

const isNewUser = (userId) => {
  if (!userId) return false;
  return userId >= NEW_USER_THRESHOLD_2026;
};

// Enhanced function that considers reduced starter permissions for new users
const hasSufficientPrivilegesWithNewUserRestrictions = (
  user,
  minimumMembershipLevel: number
): boolean => {
  if (!user) return false;

  const roles = user[FFH_ROLES] || [];
  const userId = user?.[FFH_EXTERNAL_ID];

  if (!userId) return false;

  const userHighestLevel = Math.max(
    ...roles.map((role) => ROLE_LEVELS[role] || 0),
    0
  );

  // Special handling for starter users based on their user ID
  if (userHighestLevel === ROLE_LEVELS.STARTER) {
    // Legacy starter users (ID < 450k) get upgraded access - can access pro/ultra
    if (!isNewUser(userId)) {
      return true; // Legacy starters can access everything
    }

    // New starter users (ID >= 450k) get restricted access - cannot access pro/ultra
    if (isNewUser(userId)) {
      return minimumMembershipLevel <= ROLE_LEVELS.STARTER;
    }
  }

  // Default case: check privileges normally for all other users (FREE, PRO, ULTRA)
  return userHighestLevel >= minimumMembershipLevel;
};

const getMembershipIds = (user: Auth0UserInfo | undefined): number[] => {
  if (!user) return [];
  const roles = user[FFH_ROLES] || [];
  const roleToMembershipId = {
    free: MEMBERSHIPS.FREE_ID,
    starter: MEMBERSHIPS.STARTER_YEARLY_ID,
    pro: MEMBERSHIPS.PRO_YEARLY_ID,
    ultra: MEMBERSHIPS.ULTRA_YEARLY_ID
  };
  if (Array.isArray(roles) && roles.length > 0) {
    return roles
      .map((role) => roleToMembershipId[role.toLowerCase()])
      .filter(Boolean);
  }

  return [];
};

const getExternalUserId = (
  user: Auth0UserInfo | undefined
): number | undefined => {
  if (!user) return undefined;

  const externalId = user[FFH_EXTERNAL_ID];
  if (typeof externalId === 'number' && !isNaN(externalId)) {
    return externalId;
  }

  return undefined;
};

const getProfileId = (user: Auth0UserInfo | undefined): string | undefined => {
  if (!user) return undefined;

  const profileId = user[FFH_PROFILE_ID];
  if (typeof profileId === 'string' && profileId.length > 0) {
    return profileId;
  }

  return undefined;
};

const getUserSubscriptionInfo = (
  user: Auth0UserInfo | undefined
):
  | Auth0UserInfo['https://fantasyfootballhub.co.uk/subscription']
  | undefined => {
  if (!user) return undefined;

  const subscription = user['https://fantasyfootballhub.co.uk/subscription'];
  if (subscription) {
    return subscription;
  }

  return undefined;
};

const convertSlugToURI = (slug) => {
  if (!Array.isArray(slug)) return slug;
  let uri = '';

  slug.forEach((s) => {
    uri += `${s}/`;
  });

  return uri;
};

const isUserAuthenticated = (user: Auth0UserInfo | undefined | null) => {
  return !!user?.sub;
};

/**
 * Normalises a caller-supplied path into the canonical form: query string and
 * fragment stripped (filter and pagination views canonicalise to the base
 * page), leading slash enforced, trailing slash removed. Callers should pass
 * a resolved path (`router.asPath` on dynamic routes) - a bare WordPress slug
 * still produces a syntactically valid URL here, but pointing at the wrong
 * path, so it is normalised rather than trusted.
 */
const toCanonicalPath = (pathname = '') => {
  const [bare] = pathname.split(/[?#]/);
  const withLeadingSlash = bare.startsWith('/') ? bare : `/${bare}`;
  return withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')
    ? withLeadingSlash.slice(0, -1)
    : withLeadingSlash;
};

const processSEO = (pathname = '') => {
  return {
    replace: (domNode) => {
      if (domNode.attribs && domNode.attribs.rel === 'canonical') {
        return (
          <link
            rel="canonical"
            href={`${FFH_HOSTNAME}${pathname}`}
            key="canonical"
          />
        );
      }
      // Every property meta is re-emitted carrying its property as its key,
      // because next/head only de-duplicates metas by value for the attributes
      // in its METATYPES list - name, httpEquiv, charSet and itemProp - and
      // `property` is not one of them. Open Graph tags therefore de-duplicate
      // on an exact key match alone, and without one the `_app` defaults render
      // alongside the page's own og tags, generic value first (FFH-2443).
      if (domNode.attribs && domNode.attribs.property) {
        const { property, content } = domNode.attribs;
        return (
          <meta
            property={property}
            content={
              property === 'og:url' ? `${FFH_HOSTNAME}${pathname}` : content
            }
            key={property}
          />
        );
      }
      if (domNode.attribs && domNode.attribs.class === 'yoast-schema-graph') {
        const data = domNode.children[0].data.toString();
        domNode.children[0].data = normaliseSchemaGraphUrls(
          data
            .replace(
              /cms\.fantasyfootballhub\.co\.uk\/(?!wp)/g,
              'fantasyfootballhub.co.uk/'
            )
            .replace(
              'https://fantasyfootballhub.co.uk/?s={search_term_string}',
              'https://fantasyfootballhub.co.uk/search?query={search_term_string}'
            )
        );
        return { domNode };
      }
    }
  };
};

const isImage = (domNode) => {
  if (isTagByName(domNode, 'img')) {
    return true;
  }
  return false;
};

const isParagraphImage = (domNode) => {
  if (
    isTagByName(domNode, 'p') &&
    domNode.children &&
    domNode.children[0]?.name == 'img'
  ) {
    return true;
  }
  return false;
};

const isImageLink = (domNode) => {
  if (
    isTagByName(domNode, 'a') &&
    domNode.children &&
    domNode.children[0]?.name == 'img'
  ) {
    return true;
  }
  return false;
};

const isOptimisationCandidate = (img) => {
  return !img.attribs.src.includes('AgreeDisagree');
};

const getAuthorNameAsAffiliateString = (authorName) => {
  return authorName.toLowerCase().replace(/[_@. ]/, '');
};

const processSitemap = (sitemap) => {
  return sitemap
    .replace(
      /cms\.fantasyfootballhub\.co\.uk\//g,
      'www.fantasyfootballhub.co.uk/'
    )
    .replace(/\/<\/loc>/g, '</loc>');
};

const TeamNamesDict = {
  1: {
    long: 'Arsenal',
    short: 'ARS'
  },
  2: {
    long: 'Aston Villa',
    short: 'AVL'
  },
  3: {
    long: 'Burnley',
    short: 'BUR'
  },
  4: {
    long: 'Bournemouth',
    short: 'BOU'
  },
  5: {
    long: 'Brentford',
    short: 'BRE'
  },
  6: {
    long: 'Brighton',
    short: 'BHA'
  },
  7: {
    long: 'Chelsea',
    short: 'CHE'
  },
  8: {
    long: 'Crystal Palace',
    short: 'CRY'
  },
  9: {
    long: 'Everton',
    short: 'EVE'
  },
  10: {
    long: 'Fulham',
    short: 'FUL'
  },
  11: {
    long: 'Leeds',
    short: 'LEE'
  },
  12: {
    long: 'Liverpool',
    short: 'LIV'
  },
  13: {
    long: 'Man City',
    short: 'MCY'
  },
  14: {
    long: 'Man United',
    short: 'MUN'
  },
  15: {
    long: 'Newcastle',
    short: 'NEW'
  },
  16: {
    long: 'Forest',
    short: 'NOT'
  },
  17: {
    long: 'Sunderland',
    short: 'SUN'
  },
  18: {
    long: 'Spurs',
    short: 'TOT2'
  },
  19: {
    long: 'West Ham',
    short: 'WHM'
  },
  20: {
    long: 'Wolves',
    short: 'WLV'
  }
};

const getTeamLongName = (teamNumber: number) => {
  if (TeamNamesDict[teamNumber].long) return TeamNamesDict[teamNumber].long;

  return 'Unknown';
};

const getTeamShortName = (teamNumber: number) => {
  if (TeamNamesDict[teamNumber]?.short) return TeamNamesDict[teamNumber]?.short;

  return teamNumber;
};

const getTeamIdFromClubName = (clubName: string): number | undefined => {
  if (!clubName) return undefined;
  for (const [id, team] of Object.entries(TeamNamesDict)) {
    if (team.long.toLowerCase() === clubName.toLowerCase()) {
      return Number(id);
    }
  }
  return undefined;
};

const getTeamsSelectValues = () => {
  return Object.values(TeamNamesDict).map((item) => {
    let value: string;
    let short: string;

    switch (item.short) {
      case 'BTN':
        short = 'BHA';
        value = item.long;
        break;
      case 'LEI':
        short = item.short;
        value = 'Leicester';
        break;
      case 'MCY':
        short = 'MCI';
        value = item.long;
        break;
      case 'MUN':
        short = item.short;
        value = 'Man Utd';
        break;
      case 'NOT':
        value = "Nott'm Forest";
        short = 'NFO';
        break;
      case 'SOT':
        value = item.long;
        short = 'SOU';
        break;
      case 'TOT2':
        value = item.long;
        short = 'TOT';
        break;
      case 'WHM':
        value = item.long;
        short = 'WHU';
        break;
      case 'WLV':
        value = item.long;
        short = 'WOL';
        break;
      case 'LUT':
        short = item.short;
        value = 'Luton';
        break;
      case 'SHU':
        short = item.short;
        value = 'Sheffield Utd';
        break;
      default:
        value = item.long;
        short = item.short;
        break;
    }
    return {
      short,
      label: item.long,
      value
    };
  });
};

const getLinkDomain = (domain) => {
  const isApp = typeof window !== 'undefined' && window?.platform === 'app';
  if (isApp) {
    return '';
  }
  return domain;
};

const getisAppView = () => {
  const isApp = typeof window !== 'undefined' && window?.platform === 'app';
  return isApp;
};

const isEven = (value) => {
  if (value % 2 == 0) return true;
  else return false;
};

const formatCost = (cost, hidePoundSign = false) => {
  return `${hidePoundSign ? '' : '£'}${(cost / 10).toFixed(1)}`;
};

const getStringsAsKebabCase = (str) => str.toLowerCase().split(' ').join('-');

const getPriceAsCurrency = (price, currencySymbol) =>
  `${currencySymbol || '£'} ${Math.round(Number(price)) / 100}`;

const getMembershipTitleFromId = (id) => {
  const title = Object.keys(MEMBERSHIPS).find((key) => MEMBERSHIPS[key] === id);
  return title?.split('_').join(' ').split('ID')[0];
};

const isSubscriptionPaymentViaAppGateway = (subscription) => {
  return APP_PAYMENT_GATEWAYS.includes(subscription.gateway);
};

const isProd = process.env.NEXT_PUBLIC_VERCEL_ENV === 'production';

/**
 * Renders a page's Yoast head plus a self-generated canonical link.
 *
 * The canonical is built here rather than rewritten from Yoast because
 * WPGraphQL's `fullHead` does not include one at all: as of August 2026 no
 * page on the production site shipped a canonical tag (FFH-2443). The
 * `og:url` Yoast does provide is rewritten onto the same normalised path so
 * the two can never disagree.
 */
const getYoastHeadSEO = (headSEO: string, pathname: string) => {
  const canonicalPath = toCanonicalPath(pathname);
  const canonical = (
    <link
      rel="canonical"
      href={`${FFH_HOSTNAME}${canonicalPath}`}
      key="canonical"
    />
  );

  if (!headSEO) return canonical;

  const parsed = parse(headSEO, processSEO(canonicalPath));
  return [canonical, ...(Array.isArray(parsed) ? parsed : [parsed])];
};

const getLatestTransaction = (transactions: Transaction[]) => {
  const latestTransaction = transactions?.reduce((prev, current) =>
    prev.created_at > current.created_at ? prev : current
  );
  return latestTransaction;
};

const gameweekDeadlineHasPassed = (deadlineDate: string | null) => {
  if (yn(process.env.NEXT_PUBLIC_IS_TEST)) return false;
  if (!deadlineDate) return false;
  const gameweekDeadline = dayjs(deadlineDate).tz('Europe/London');
  const timeNow = dayjs().tz('Europe/London');
  const minutesRemainingUntilDeadline = gameweekDeadline.diff(
    timeNow,
    'minutes'
  );

  return minutesRemainingUntilDeadline < 1;
};

const validateCaptcha = (action): Promise<string> => {
  return new Promise((res) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(`${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_V3}`, {
          action
        })
        .then((token) => {
          return res(token);
        });
    });
  });
};

type GetRecommendedTransferTitleParams = {
  transfer: any;
  isCustom: boolean;
  index?: number;
};

const getRecommendedTransferTitle = ({
  transfer,
  isCustom,
  index
}: GetRecommendedTransferTitleParams) => {
  if (isCustom)
    return `${
      index && index > 0 ? 'Alternative custom' : 'Custom'
    } transfer suggestion`;
  if (transfer.meta.suggested) {
    if (transfer.meta.gw_index === 1) {
      return 'Recommended';
    } else {
      return 'Recommended';
    }
  } else if (transfer.status !== 'Infeasible') {
    return 'Alternative option';
  } else {
    return '';
  }
};

const getPositionLabelFromPositionId = ({
  positionId,
  isShortHand
}: {
  positionId: number;
  isShortHand: boolean;
}) => {
  if (positionId === 1) {
    return isShortHand ? 'GK' : 'Goalkeeper';
  }
  if (positionId === 2) {
    return isShortHand ? 'DEF' : 'Defender';
  }
  if (positionId === 3) {
    return isShortHand ? 'MID' : 'Midfielder';
  }
  if (positionId === 4) {
    return isShortHand ? 'FWD' : 'Forward';
  }
};

const playerPositionOptions = [
  { value: 0, label: 'All' },
  { value: 1, label: 'GK' },
  { value: 2, label: 'DEF' },
  { value: 3, label: 'MID' },
  { value: 4, label: 'FWD' }
];

const playerIsStagedForTransferOut = (
  transferPlayersOut: PlayerType[],
  player: PlayerType
) => {
  return transferPlayersOut?.some(
    (playerOut) => player?.code === playerOut?.code
  );
};

const stripAccentsAndSpecialCharacters = (string: string): string => {
  // Strip any accents.
  string = string.normalize('NFKD').replace(/[\u0300-\u036F]/g, '');

  // Replace any special characters.
  const specialCharacters = {
    Ø: 'O',
    æ: 'ae',
    ø: 'o',
    å: 'a'
  };
  for (const specialCharacter in specialCharacters) {
    string = string.replace(
      new RegExp(specialCharacter, 'g'),
      specialCharacters[specialCharacter]
    );
  }

  return string;
};

const isUltraMember = (session) => {
  return hasSufficientPrivileges(
    session,
    PAID_MEMBERSHIP_LEVELS[MEMBERSHIPS.ULTRA_YEARLY_ID].LEVEL
  );
};

/**
 * The one shirt still bundled with the app.
 *
 * Every team's kit now comes from the API, versioned so a replacement reaches
 * caches on its own. This covers the cases where there is no kit to show at
 * all — an empty squad slot, enrichment still loading, a code the API does not
 * recognise — so it is a placeholder rather than artwork, and needs no season
 * in its path and no hand-maintained cache buster.
 */
const BLANK_SHIRT_PATH = '/images/team-shirts/blank.png';

const isSeasonOpen = (currentGameweek: number | undefined) => {
  if (
    typeof currentGameweek !== 'number' ||
    currentGameweek < 1 ||
    currentGameweek > 38
  ) {
    return false;
  }
  return true;
};

const hasReviewableGameweeks = (currentGameweek: number | undefined) => {
  if (
    typeof currentGameweek !== 'number' ||
    currentGameweek <= 1 ||
    currentGameweek > 38
  ) {
    return false;
  }
  return true;
};

const getTierAndTermByPlanId = (
  planId
): { tier: string; term: string | null } | null => {
  // Convert planId to number if it's a string
  const numericPlanId =
    typeof planId === 'string' ? parseInt(planId, 10) : planId;

  // Find the key that matches the planId
  const entry = Object.entries(TIER_TO_MEMBERSHIP).find(
    ([, value]) => value === numericPlanId
  );

  if (!entry) {
    return null;
  }

  const [key] = entry;

  // Handle free tier special case
  if (key === 'free') {
    return { tier: 'free', term: null };
  }

  // Split the key to get tier and term
  //
  // FFH-1744: the alt-pricing keys have three segments (starter_yearly_alt), so
  // they return null here. That is deliberate and currently unreachable — the
  // only caller is useSubscriptionProration, which is passed the plan the user
  // is switching TO, and the account change-plan flow can only ever name a
  // control plan (the API leaves the alt plans out of its own TIER_TO_MEMBERSHIP
  // for exactly that reason). If an alt id ever did arrive, the caller throws
  // rather than quoting a price, which is the safe direction.
  const parts = key.split('_');
  if (parts.length !== 2) {
    return null;
  }

  const [tier, term] = parts;
  return { tier, term };
};

const STRIPE_PROVIDER = 'stripe';

// Statuses under which the claim describes a subscription that has ended.
// Mirrors VOID_SUBSCRIPTION_STATUSES in the API's subscription-claim.ts and
// getEffectiveTier in the Auth0 post-login Action: the subscriptions service
// spells it cancelled, Stripe spells it canceled, so accept both.
const VOID_SUBSCRIPTION_STATUSES = new Set([
  'cancelled',
  'canceled',
  'expired'
]);

// Where a member who already has a plan is sent instead of checkout. The
// account page holds the change, cancel and reactivate controls for every
// provider, so it is the one destination that is correct for all of them.
const EXISTING_PLAN_STOP_PATH = '/account';

type CheckoutPathOptions = {
  returnPath?: CheckoutReturnPath;
  loginHintEmail?: string;
  emailOnlySignup?: boolean;
  trialPeriodDays?: number;
  /**
   * FFH-1743: the coupon this funnel arm asks checkout to apply when the
   * visitor brought none of their own. Ranks BELOW `?coupon=` and the captured
   * session coupon — see resolveCheckoutCoupon in lib/checkout/arm-coupon.ts.
   */
  armCoupon?: string;
};

// An options bag rather than positional arguments: this had grown to six
// parameters, so call sites read `getCheckoutPath(user, 42, undefined,
// undefined, undefined, 7)` and adding a seventh made the next one unreadable.
// `user` and `planId` stay positional because every caller passes both.
const getCheckoutPath = (
  user: Auth0UserInfo | undefined,
  planId,
  {
    returnPath,
    loginHintEmail,
    emailOnlySignup,
    trialPeriodDays,
    armCoupon
  }: CheckoutPathOptions = {}
) => {
  // FFH-1742: the trial arm's paywall asks checkout for a free trial. Rides
  // inside returnTo the same way returnPath does, so it survives the Auth0
  // signup redirect. The API is authoritative on the value (clamped, Ultra
  // excluded); 0/undefined omits the param entirely.
  const trialParam =
    trialPeriodDays && trialPeriodDays > 0
      ? `&trialDays=${trialPeriodDays}`
      : '';

  // FFH-1743: the arm's default coupon, riding the same rail as trialDays. The
  // API is authoritative here too — an unresolvable name falls back to the
  // configured default rather than charging full price.
  const armCouponParam = armCoupon
    ? `&${ARM_COUPON_QUERY_PARAM}=${encodeURIComponent(armCoupon)}`
    : '';

  // With a returnPath (or trial, or arm coupon) the returnTo value carries
  // multiple query params, so it must be URL-encoded — an unencoded & would
  // bind the second param to /auth/login instead of the returnTo target.
  // Without any of them, the output stays byte-identical to the original for
  // existing callers.
  const checkoutQuery = `/checkout-stripe?planId=${planId}${
    returnPath ? `&returnPath=${returnPath}` : ''
  }${trialParam}${armCouponParam}`;
  const checkoutTarget =
    returnPath || trialParam || armCouponParam
      ? encodeURIComponent(checkoutQuery)
      : checkoutQuery;

  // login_hint prefills the email field on the Auth0 universal login signup
  // screen. It's a hint only — the user can still edit it there.
  const loginHint = loginHintEmail
    ? `&login_hint=${encodeURIComponent(loginHintEmail)}`
    : '';

  // Forcing the database connection makes universal login skip the
  // connection picker, so the Google/Apple buttons never render and funnel
  // users are nudged to sign up with the email captured earlier. Signup
  // link only — the update-stripe branch below must stay connection-free
  // or social-login subscribers couldn't reach it.
  const connection = emailOnlySignup
    ? '&connection=Username-Password-Authentication'
    : '';

  // ext-planId is forwarded into the Auth0 transaction context so the
  // universal login partials can show a plan-specific summary banner.
  const signupLink = `/auth/login?screen_hint=signup&ext-planId=${planId}${loginHint}${connection}&returnTo=${checkoutTarget}`;

  if (!user) {
    return signupLink;
  }

  const subscription = getUserSubscriptionInfo(user);

  // FFH-2592: the test used to be `stripe && active`, and everything else
  // fell through to the signup link. That link SSO-redirects an already
  // logged-in member straight into /checkout-stripe, which bought them a
  // second subscription while the first kept renewing. The default is now
  // inverted: only a member with no paid signal at all reaches checkout.
  //
  // Two independent signals count as paid, because either can be missing on
  // its own. The subscription claim is absent whenever the post-login
  // Action's lookup fails, which is indistinguishable from having no
  // subscription; the roles claim is minted separately and survives that.
  //
  // A claim in a void status is not a paid signal: a lapsed member must be
  // able to buy again. The one case this cannot see, cancelled but still
  // inside the paid period, is caught by the API's 409 (FFH-2591), which the
  // checkout page routes to the same place the CTA would have.
  const hasLiveSubscriptionClaim =
    Boolean(subscription?.source) &&
    !VOID_SUBSCRIPTION_STATUSES.has(subscription?.status?.toLowerCase() ?? '');

  if (!hasLiveSubscriptionClaim && !isPaidMember(user)) {
    return signupLink;
  }

  return `/auth/login?returnTo=${getExistingPlanPath(user, planId)}`;
};

/**
 * Where a member who already holds a plan belongs, without the /auth/login
 * wrapper that getCheckoutPath adds.
 *
 * FFH-2592: exported so /checkout-stripe can send the API's 409
 * `has_live_subscription` to exactly the same place the CTA would have, rather
 * than inventing a second rule that can drift from this one.
 */
const getExistingPlanPath = (
  user: Auth0UserInfo | undefined,
  planId
): string => {
  // /update-stripe previews proration against a specific Stripe subscription.
  // Everyone else (PayPal, RevenueCat, or a paid role whose claim never
  // arrived) goes to their account, where the plan controls live. Sending them
  // to /update-stripe regardless would strand them on a spinner.
  return isActionableStripeSubscription(getUserSubscriptionInfo(user))
    ? `/update-stripe?planId=${planId}`
    : EXISTING_PLAN_STOP_PATH;
};

/**
 * Whether the subscription claim describes a Stripe subscription we can act
 * on, meaning both a Stripe provider and a subscription id to quote against.
 *
 * FFH-2592: deliberately says nothing about `status`. The claim reports
 * cancelled for a member who is still inside their paid period, and past_due
 * renewals only read as active by omission (ADR-0014), so a status test here
 * would refuse to serve members who are perfectly able to change plan. This
 * matches what the API itself requires for a proration preview.
 */
const isActionableStripeSubscription = (subscription): boolean =>
  // Provider casing is inconsistent coming out of Auth0, so normalise before
  // comparing, as the account page already does.
  subscription?.source?.provider?.toLowerCase() === STRIPE_PROVIDER &&
  Boolean(subscription?.source?.id);

const isInEmbeddedWebView = () => {
  const ua = navigator.userAgent || navigator.vendor;
  return /(FBAN|FBAV|Instagram|Line|WebView|TikTok)/i.test(ua);
};

const getAuthLinkPath = (path) => {
  if (isInEmbeddedWebView()) {
    if (path === '/auth/login') {
      return '/auth/login?connection=Username-Password-Authentication';
    }
    if (path === '/auth/login?screen_hint=signup') {
      return '/auth/login?screen_hint=signup&connection=Username-Password-Authentication';
    }
  }
  return path;
};

/**
 * Generates a unique event ID with type prefix
 * Format: {eventType}_{timestamp}_{randomString}
 * Example: "purchase_1703847600123_a8b2c3d4"
 */
const generateEventId = (eventType = 'event') => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 11);
  return `${eventType}_${timestamp}_${randomString}`;
};

export {
  sleep,
  isEmbeddedVideo,
  isLinkToArticle,
  isCurrentPath,
  isCurrentlyActive,
  isInActiveHierachy,
  isParagraphWrappingIframe,
  isPaidMember,
  isMatchingLink,
  convertSlugToURI,
  isUserAuthenticated,
  getMembershipIds,
  getTextFromDomNode,
  isImage,
  isParagraphImage,
  isImageLink,
  isOptimisationCandidate,
  isProd,
  getAuthorNameAsAffiliateString,
  processSitemap,
  getTeamLongName,
  getTeamShortName,
  getLinkDomain,
  isEven,
  formatCost,
  getStringsAsKebabCase,
  getPriceAsCurrency,
  getMembershipTitleFromId,
  isSubscriptionPaymentViaAppGateway,
  getTeamsSelectValues,
  getYoastHeadSEO,
  toCanonicalPath,
  hasSufficientPrivileges,
  hasAdminRole,
  getLatestTransaction,
  getIsFreeMember,
  gameweekDeadlineHasPassed,
  validateCaptcha,
  getRecommendedTransferTitle,
  getPositionLabelFromPositionId,
  getisAppView,
  playerPositionOptions,
  playerIsStagedForTransferOut,
  stripAccentsAndSpecialCharacters,
  isUltraMember,
  BLANK_SHIRT_PATH,
  isSeasonOpen,
  hasReviewableGameweeks,
  getExternalUserId,
  getProfileId,
  getUserSubscriptionInfo,
  getTierAndTermByPlanId,
  getCheckoutPath,
  getExistingPlanPath,
  isActionableStripeSubscription,
  hasSufficientPrivilegesWithNewUserRestrictions,
  getTeamIdFromClubName,
  getAuthLinkPath,
  isInEmbeddedWebView,
  generateEventId,
  getCleverTapMembershipTier,
  getCleverTapTierName
};
